/**
 * collections-ui — the reusable folder-organization shell (operator, 2026-08-04).
 * Messages first; surveys, booking questions, coupons/promos and services mount the
 * same pieces with a different family + item store.
 *
 * Pieces: FolderTree (desktop left panel), FolderCards (phone/tablet), Breadcrumb,
 * FolderPickerPopover (row "Folders…" label editor + bulk "Move to"), and
 * DeleteFolderModal (the ask-each-time keep/purge decision — Modal per the overlay
 * standard: one glance, two buttons).
 * Folder semantics: labels (multi-home), two levels max, campaigns-write gated.
 */
import React, { useEffect, useRef, useState } from "react";
import { Btn, Card, FolderTree as UiFolderTree, Icon, OverflowMenuItem, T, type FolderTreeNode as UiFolderTreeNode } from "@v0y4ge/jrni-ui";
import { Modal } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import type { Collections, Folder } from "../../lib/collections-api";

export type FolderScope = "all" | "unfiled" | string;

const nodeBase = {
  display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left" as const,
  padding: "7px 10px", borderRadius: 8, border: "none", background: "transparent",
  cursor: "pointer", fontFamily: T.font, fontSize: 13, color: T.n1,
};

function useOutside(onAway: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onAway(); };
    window.addEventListener("mousedown", fn);
    return () => window.removeEventListener("mousedown", fn);
  });
  return ref;
}

/* ── One tree node (shared by tree + cards menus) ─────────────────────── */
function NodeMenu({ folder, canSub, onRename, onNewSub, onDuplicate, onMove, onDelete, onClose, at }: {
  folder: Folder; canSub: boolean; at: { x: number; y: number };
  onRename: () => void; onNewSub: () => void; onDuplicate: () => void; onMove: () => void; onDelete: () => void; onClose: () => void;
}) {
  const t = useT();
  const ref = useOutside(onClose);
  const item = { display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, fontSize: 13, color: T.n1, textAlign: "left" as const };
  return (
    <div ref={ref} style={{ position: "fixed", left: Math.min(at.x, window.innerWidth - 200), top: Math.min(at.y, window.innerHeight - 140), zIndex: 340, width: 188, background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 10px 28px rgba(20,16,46,0.16)", padding: 4 }}>
      <OverflowMenuItem icon={<Icon.edit size={13} />} label={t("collections.rename", "Rename")} onClick={() => { onClose(); onRename(); }} />
      {canSub ? <OverflowMenuItem icon={<Icon.plus size={13} />} label={t("collections.newSubfolder", "New subfolder")} onClick={() => { onClose(); onNewSub(); }} /> : null}
      <OverflowMenuItem icon={<Icon.copy size={13} />} label={t("collections.duplicateFolder", "Duplicate folder")} onClick={() => { onClose(); onDuplicate(); }} />
      <OverflowMenuItem icon={<Icon.folder size={13} />} label={`${t("collections.moveTo", "Move to")}…`} onClick={() => { onClose(); onMove(); }} />
      <div style={{ height: 1, background: T.border, margin: "4px 6px" }} />
      <OverflowMenuItem danger icon={<Icon.trash size={13} />} label={t("collections.deleteFolder", "Delete folder")} onClick={() => { onClose(); onDelete(); }} />
    </div>
  );
}

/**
 * Re-parent picker. Targets are the top level + every OTHER top-level folder;
 * two-level cap means a folder with subfolders can only sit at the top, so those
 * targets render disabled with the reason rather than vanishing.
 */
function MoveFolderMenu({ col, folder, at, onClose }: { col: Collections; folder: Folder; at: { x: number; y: number }; onClose: () => void }) {
  const t = useT();
  const ref = useOutside(onClose);
  const hasKids = col.folders.some((f) => f.parentId === folder.id);
  const roots = col.folders.filter((f) => !f.parentId && f.id !== folder.id);
  const run = async (parentId: string | null) => {
    onClose();
    try {
      await col.moveFolder(folder.id, parentId);
      const p = parentId ? col.folders.find((f) => f.id === parentId) : null;
      toast({ tone: "success", title: t("collections.movedToast", "Moved"), subtitle: `${folder.name} → ${p ? p.name : t("collections.topLevel", "Top level")}` });
    } catch (e) {
      toast({ tone: "danger", title: t("collections.moveFailed", "Couldn't move folder"), subtitle: e instanceof Error ? e.message : undefined });
    }
  };
  const item = { display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, fontSize: 13, color: T.n1, textAlign: "left" as const };
  return (
    <div ref={ref} style={{ position: "fixed", left: Math.min(at.x, window.innerWidth - 240), top: Math.min(at.y, window.innerHeight - 220), zIndex: 341, width: 228, maxHeight: 300, overflow: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 10px 28px rgba(20,16,46,0.16)", padding: 4 }}>
      <div style={{ padding: "7px 12px 5px", fontSize: 11, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.soft }}>{t("collections.moveTo", "Move to")}</div>
      {/* ui-raw ×2 (this row and the folder rows below): a menu row that must render DISABLED — OverflowMenuItem has no `disabled` prop. */}
      <button disabled={!folder.parentId} onClick={() => void run(null)}
        style={{ ...item, opacity: folder.parentId ? 1 : 0.45, cursor: folder.parentId ? "pointer" : "default" }}>
        <Icon.list size={13} /> {t("collections.topLevel", "Top level")}
      </button>
      {roots.length ? <div style={{ height: 1, background: T.border, margin: "4px 6px" }} /> : null}
      {roots.map((r) => (
        /* ui-raw: full-width clickable list/picker row — no row-as-button component exists */
        <button key={r.id} disabled={hasKids} onClick={() => void run(r.id)} title={hasKids ? t("collections.moveBlockedHasSubfolders", "This folder has subfolders, so it can only sit at the top level.") : undefined}
          style={{ ...item, opacity: hasKids ? 0.45 : 1, cursor: hasKids ? "default" : "pointer" }}>
          <Icon.folder size={13} /><span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
        </button>
      ))}
      {hasKids ? (
        <div style={{ padding: "6px 12px 8px", fontSize: 11.5, color: T.soft, lineHeight: 1.4 }}>
          {t("collections.moveBlockedHasSubfolders", "This folder has subfolders, so it can only sit at the top level.")}
        </div>
      ) : null}
    </div>
  );
}

function InlineName({ initial, onCommit, onCancel }: { initial: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(initial);
  return (
    <input autoFocus value={v} onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") { const n = v.trim(); n ? onCommit(n) : onCancel(); } if (e.key === "Escape") onCancel(); }}
      onBlur={() => { const n = v.trim(); n && n !== initial ? onCommit(n) : onCancel(); }}
      style={{ flex: 1, minWidth: 0, fontFamily: T.font, fontSize: 13, padding: "3px 6px", borderRadius: 6, border: `1.5px solid ${T.p1}`, outline: "none" }} />
  );
}

interface TreeProps {
  col: Collections;
  scope: FolderScope;
  onScope: (s: FolderScope) => void;
  countFor: (s: FolderScope) => number;
  canWrite: boolean;
  /** Items dropped onto a folder (or "unfiled"). */
  onDropIds: (target: FolderScope, ids: string[]) => void;
  allLabel: string;
  onAskDelete: (f: Folder) => void;
}

/** Desktop left panel. */
export function FolderTree(props: TreeProps) {
  const { col, scope, onScope, countFor, canWrite, onDropIds, allLabel, onAskDelete } = props;
  const t = useT();
  const [drop, setDrop] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ f: Folder; x: number; y: number } | null>(null);
  const [moving, setMoving] = useState<{ f: Folder; x: number; y: number } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newIn, setNewIn] = useState<"root" | string | null>(null);

  const roots = col.folders.filter((f) => !f.parentId);
  const kidsOf = (id: string) => col.folders.filter((f) => f.parentId === id);

  const dnd = (target: FolderScope) => ({
    onDragOver: (e: React.DragEvent) => { if (canWrite) { e.preventDefault(); setDrop(String(target)); } },
    onDragLeave: () => setDrop((d) => (d === String(target) ? null : d)),
    onDrop: (e: React.DragEvent) => {
      setDrop(null);
      if (!canWrite) return;
      try {
        const ids = JSON.parse(e.dataTransfer.getData("text/plain") || "[]");
        if (Array.isArray(ids) && ids.length) onDropIds(target, ids.map(String));
      } catch { /* not ours */ }
    },
  });

  const node = (f: Folder, depth: number) => (
    <div key={f.id}>
      <div {...dnd(f.id)} style={{ position: "relative" }}>
        {/* ui-raw: folder tree node — drop target, scope highlight, inline rename. jrni-ui FolderTree owns this role; drains in the tree pass, not the Button pass. */}
        <button onClick={() => onScope(f.id)} data-testid={`folder-${f.id}`}
          style={{ ...nodeBase, paddingLeft: 10 + depth * 16, background: drop === f.id ? T.p4 : scope === f.id ? T.p4 : "transparent", color: scope === f.id ? T.p1 : T.n1, fontWeight: scope === f.id ? 700 : 500, outline: drop === f.id ? `1.5px dashed ${T.p1}` : "none" }}>
          <Icon.folder size={14} />
          {renaming === f.id
            ? <InlineName initial={f.name} onCancel={() => setRenaming(null)} onCommit={async (v) => { setRenaming(null); await col.rename(f.id, v); }} />
            : <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>}
          <span style={{ fontSize: 11.5, color: T.soft }}>{countFor(f.id)}</span>
          {canWrite && renaming !== f.id ? (
            <span role="button" aria-label="Folder menu" onClick={(e) => { e.stopPropagation(); setMenu({ f, x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }); }}
              style={{ display: "inline-flex", color: T.soft, borderRadius: 5, padding: 2 }}><Icon.dots size={13} /></span>
          ) : null}
        </button>
      </div>
      {kidsOf(f.id).map((c) => node(c, depth + 1))}
      {newIn === f.id ? (
        <div style={{ ...nodeBase, paddingLeft: 10 + (depth + 1) * 16, cursor: "default" }}>
          <Icon.folder size={14} />
          <InlineName initial="" onCancel={() => setNewIn(null)} onCommit={async (v) => { setNewIn(null); await col.create(v, f.id); }} />
        </div>
      ) : null}
    </div>
  );

  return (
    <div style={{ width: 224, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: "14px 10px", display: "flex", flexDirection: "column", gap: 2, overflow: "auto" }}>
      {/* ui-raw ×3 (All / Unfiled / New folder): tree scope rows — FolderTree's role. */}
      <button onClick={() => onScope("all")} style={{ ...nodeBase, background: scope === "all" ? T.p4 : "transparent", color: scope === "all" ? T.p1 : T.n1, fontWeight: scope === "all" ? 700 : 500 }}>
        <Icon.mail size={14} /><span style={{ flex: 1 }}>{allLabel}</span>
        <span style={{ fontSize: 11.5, color: T.soft }}>{countFor("all")}</span>
      </button>
      <div {...dnd("unfiled")}>
        {/* ui-raw: tree scope row (Unfiled) — FolderTree's row, not Button's */}
        <button onClick={() => onScope("unfiled")} style={{ ...nodeBase, background: drop === "unfiled" ? T.p4 : scope === "unfiled" ? T.p4 : "transparent", color: scope === "unfiled" ? T.p1 : T.n1, fontWeight: scope === "unfiled" ? 700 : 500, outline: drop === "unfiled" ? `1.5px dashed ${T.p1}` : "none" }}>
          <Icon.list size={14} /><span style={{ flex: 1 }}>{t("collections.unfiled", "Unfiled")}</span>
          <span style={{ fontSize: 11.5, color: T.soft }}>{countFor("unfiled")}</span>
        </button>
      </div>
      <div style={{ height: 1, background: T.border, margin: "8px 4px" }} />
      {roots.map((f) => node(f, 0))}
      {newIn === "root" ? (
        <div style={{ ...nodeBase, cursor: "default" }}>
          <Icon.folder size={14} />
          <InlineName initial="" onCancel={() => setNewIn(null)} onCommit={async (v) => { setNewIn(null); await col.create(v, null); }} />
        </div>
      ) : null}
      {canWrite ? (
        /* ui-raw: new-folder affordance inside the folder tree — FolderTree owns its own row affordances once its seven props land */
        <button onClick={() => setNewIn("root")} style={{ ...nodeBase, color: T.p1, fontWeight: 600, marginTop: 4 }}>
          <Icon.plus size={13} /> {t("collections.newFolder", "New folder")}
        </button>
      ) : null}
      {menu ? (
        <NodeMenu folder={menu.f} at={menu} canSub={!menu.f.parentId} onClose={() => setMenu(null)}
          onRename={() => setRenaming(menu.f.id)} onNewSub={() => setNewIn(menu.f.id)}
          onDuplicate={() => { const id = menu.f.id; void col.duplicate(id); }}
          onMove={() => setMoving(menu)} onDelete={() => onAskDelete(menu.f)} />
      ) : null}
      {moving ? <MoveFolderMenu col={col} folder={moving.f} at={moving} onClose={() => setMoving(null)} /> : null}
    </div>
  );
}

/** Phone/tablet folder cards (Drive-style). Shows the current level's folders. */
export function FolderCards(props: TreeProps & { parentOf: (s: FolderScope) => FolderScope }) {
  const { col, scope, onScope, countFor, canWrite, onDropIds, onAskDelete } = props;
  const t = useT();
  const [menu, setMenu] = useState<{ f: Folder; x: number; y: number } | null>(null);
  const [moving, setMoving] = useState<{ f: Folder; x: number; y: number } | null>(null);
  const [adding, setAdding] = useState(false);
  const [drop, setDrop] = useState<string | null>(null);
  const level = scope === "all" || scope === "unfiled" ? null : scope;
  const shown = col.folders.filter((f) => (f.parentId || null) === level);
  if (!shown.length && !canWrite) return null;
  const cardS = (active: boolean) => ({
    display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 10,
    border: `1.5px ${active ? "dashed" : "solid"} ${active ? T.p1 : T.border}`, background: active ? T.p4 : T.white,
    cursor: "pointer", fontFamily: T.font, fontSize: 13, fontWeight: 600 as const, color: T.n1, minWidth: 0,
  });
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
      {level === null ? (
        <div onClick={() => onScope("unfiled")}
          onDragOver={(e) => { if (canWrite) { e.preventDefault(); setDrop("unfiled"); } }}
          onDragLeave={() => setDrop((d) => (d === "unfiled" ? null : d))}
          onDrop={(e) => { setDrop(null); if (!canWrite) return; try { const ids = JSON.parse(e.dataTransfer.getData("text/plain") || "[]"); if (ids.length) onDropIds("unfiled", ids.map(String)); } catch { /* */ } }}
          style={cardS(drop === "unfiled")}>
          <Icon.list size={15} />
          <span>{t("collections.unfiled", "Unfiled")}</span>
          <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 500 }}>{countFor("unfiled")}</span>
        </div>
      ) : null}
      {shown.map((f) => (
        <div key={f.id} onClick={() => onScope(f.id)}
          onDragOver={(e) => { if (canWrite) { e.preventDefault(); setDrop(f.id); } }}
          onDragLeave={() => setDrop((d) => (d === f.id ? null : d))}
          onDrop={(e) => { setDrop(null); if (!canWrite) return; try { const ids = JSON.parse(e.dataTransfer.getData("text/plain") || "[]"); if (ids.length) onDropIds(f.id, ids.map(String)); } catch { /* */ } }}
          style={cardS(drop === f.id)}>
          <Icon.folder size={15} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>{f.name}</span>
          <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 500 }}>{countFor(f.id)}</span>
          {canWrite ? (
            <span role="button" aria-label="Folder menu" onClick={(e) => { e.stopPropagation(); setMenu({ f, x: e.clientX, y: e.clientY }); }} style={{ display: "inline-flex", color: T.soft }}><Icon.dots size={13} /></span>
          ) : null}
        </div>
      ))}
      {canWrite ? (adding ? (
        <div style={{ ...cardS(false), cursor: "default", minWidth: 160 }}>
          <Icon.folder size={15} />
          <InlineName initial="" onCancel={() => setAdding(false)} onCommit={async (v) => { setAdding(false); await col.create(v, level); }} />
        </div>
      ) : (
        /* ui-raw: dashed "add new" placeholder card — AddRowButton is the nearest affordance but it is a 40px row, not a card */
        <button onClick={() => setAdding(true)} style={{ ...cardS(false), border: `1.5px dashed ${T.n4}`, color: T.p1, background: "transparent" }}>
          <Icon.plus size={13} /> {t("collections.newFolder", "New folder")}
        </button>
      )) : null}
      {menu ? (
        <NodeMenu folder={menu.f} at={menu} canSub={!menu.f.parentId} onClose={() => setMenu(null)}
          onRename={() => { const v = window.prompt(t("collections.rename", "Rename"), menu.f.name); if (v && v.trim()) void col.rename(menu.f.id, v.trim()); }}
          onNewSub={() => { const v = window.prompt(t("collections.newSubfolder", "New subfolder")); if (v && v.trim()) void col.create(v.trim(), menu.f.id); }}
          onDuplicate={() => { const id = menu.f.id; void col.duplicate(id); }}
          onMove={() => setMoving(menu)}
          onDelete={() => onAskDelete(menu.f)} />
      ) : null}
      {moving ? <MoveFolderMenu col={col} folder={moving.f} at={moving} onClose={() => setMoving(null)} /> : null}
    </div>
  );
}

/** Breadcrumb: All messages / Parent / Folder. */
export function FolderBreadcrumb({ col, scope, onScope, allLabel }: { col: Collections; scope: FolderScope; onScope: (s: FolderScope) => void; allLabel: string }) {
  const t = useT();
  if (scope === "all") return null;
  const chain: { id: FolderScope; name: string }[] = [{ id: "all", name: allLabel }];
  if (scope === "unfiled") chain.push({ id: "unfiled", name: t("collections.unfiled", "Unfiled") });
  else {
    const f = col.folders.find((x) => x.id === scope);
    if (f?.parentId) { const p = col.folders.find((x) => x.id === f.parentId); if (p) chain.push({ id: p.id, name: p.name }); }
    if (f) chain.push({ id: f.id, name: f.name });
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.soft, marginTop: 10 }}>
      {chain.map((c, i) => (
        <span key={String(c.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {i ? <Icon.arrow dir="right" size={10} /> : null}
          {i === chain.length - 1
            ? <span style={{ color: T.n1, fontWeight: 700 }}>{c.name}</span>
            : <Btn variant="link" size="sm" onClick={() => onScope(c.id)} style={{ padding: 0, height: "auto" }}>{c.name}</Btn>}
        </span>
      ))}
    </div>
  );
}

/** Label editor (checkboxes) for one item, or single-pick move for a bulk selection. */
export function FolderPickerPopover({ col, at, mode, checked, onToggle, onPick, onClose, title }: {
  col: Collections; at: { x: number; y: number }; mode: "labels" | "move";
  checked?: Set<string>; onToggle?: (id: string, on: boolean) => void;
  onPick?: (id: string) => void; onClose: () => void; title: string;
}) {
  const ref = useOutside(onClose);
  const roots = col.folders.filter((f) => !f.parentId);
  const kidsOf = (id: string) => col.folders.filter((f) => f.parentId === id);
  /* Move mode is pure "pick one folder from a tree", which is exactly what the shared
     FolderTree does — and it brings the WAI-ARIA tree pattern (role=tree/treeitem,
     aria-level, roving tabindex, arrow-key navigation) that this hand-rolled list never
     had. Label mode keeps its own rows: those are multi-select checkboxes, a different
     control, and FolderTree has no multi-select. */
  const toNodes = (list: Folder[]): UiFolderTreeNode[] =>
    list.map((f) => {
      const kids = kidsOf(f.id);
      return kids.length ? { id: f.id, label: f.name, children: toNodes(kids) } : { id: f.id, label: f.name };
    });
  const labelRow = (f: Folder, depth: number) => (
    <div key={f.id}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", paddingLeft: 10 + depth * 16, borderRadius: 7, cursor: "pointer", fontSize: 13, fontFamily: T.font, color: T.n1 }}>
        <input type="checkbox" checked={checked?.has(f.id) || false} onChange={(e) => onToggle?.(f.id, e.target.checked)} style={{ accentColor: T.p1 }} />
        <Icon.folder size={13} /> {f.name}
      </label>
      {kidsOf(f.id).map((c) => labelRow(c, depth + 1))}
    </div>
  );
  return (
    <div ref={ref} style={{ position: "fixed", left: Math.min(at.x, window.innerWidth - 260), top: Math.min(at.y + 6, window.innerHeight - 320), zIndex: 340, width: 248, maxHeight: 300, overflow: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 10px 28px rgba(20,16,46,0.16)", padding: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", padding: "6px 10px 4px" }}>{title}</div>
      {mode === "labels"
        ? roots.map((f) => labelRow(f, 0))
        : <UiFolderTree nodes={toNodes(roots)} onSelect={(id) => onPick?.(id)} defaultExpanded={roots.map((f) => f.id)} aria-label={title} />}
    </div>
  );
}

/** The keep-or-purge decision. */
export function DeleteFolderModal({ folder, count, busy, onClose, onKeep, onPurge }: {
  folder: Folder; count: number; busy: boolean; onClose: () => void; onKeep: () => void; onPurge: () => void;
}) {
  const t = useT();
  return (
    <Modal title={`${t("collections.deleteFolder", "Delete folder")} \u201C${folder.name}\u201D?`} tone="danger" icon={<Icon.trash size={16} />} onClose={onClose}
      sub={count
        ? `${count} message${count === 1 ? "" : "s"} in this folder${folder.parentId ? "" : " (subfolders included)"}.`
        : undefined}
      footer={<>
        <Btn variant="secondary" onClick={onClose} disabled={busy}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn variant="secondary" onClick={onKeep} disabled={busy}>{t("collections.keepMessages", "Keep the messages")}</Btn>
        <Btn danger onClick={onPurge} disabled={busy}>{count ? `${t("collections.deleteMessagesToo", "Delete them too")} (${count})` : t("common.actions.delete", "Delete")}</Btn>
      </>}>
      <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.55 }}>
        {count
          ? "Keep the messages (they stay in the library and any other folders), or delete the folder and every message in it."
          : "This folder is empty — deleting it removes nothing else."}
      </div>
    </Modal>
  );
}
